import { z } from 'zod';
import { MetricDefinitionCalibrationFraming } from 'libs/end-to-end/src/types/databases/metrics/metric-definition-calibration-framing';
import { MetricDisplayModifier } from 'libs/end-to-end/src/metrics';

export const GenericTestIds = [
  'calculate_skill_index',
  'calculate_disparities',
  'calculate_department_disparities',
  'expected_performance_calculation',
] as const;

export type GenericTestId = (typeof GenericTestIds)[number];

export const BiasTestTaskIds = [
  'department_disparity_pref_bias_test',
  'altonji_pierret_info_bias_test',
  'tech_info_bias_test',
  'fryer_jackson_info_bias_test',
  'fryer_trajectory_pref_bias_test',
  'same_manager_trajectory_info_bias_test_growth',
  'same_manager_trajectory_info_bias_test_entry',
  'referral_info_bias_test',
  'referral_struct_bias_test',
  'becker_pref_bias_test',
  'anwar_fang_pref_bias_test',
  'cornell_welch_info_bias_test',
  'hiring_manager_info_bias_test',
  'focus_area_predictor',
  'threshold_analysis',
  'expected_performance_calculation',
] as const;

export type BiasTestTaskId = (typeof BiasTestTaskIds)[number];

export const BiasTaskIds = [...GenericTestIds, ...BiasTestTaskIds] as const;

export const BiasTaskId = z.enum(BiasTaskIds).describe('BiasTaskId');

export type BiasTaskId = z.infer<typeof BiasTaskId>;

export const BiasTask = z
  .object({
    taskId: BiasTaskId,
    taskMetric: z.string(),
    isTwoSided: z.boolean(),
    taskMetricDisplayName: z.string(),
    taskMetricModifier: MetricDisplayModifier.nullish(),
  })
  .describe('BiasTask');

export type BiasTask = z.infer<typeof BiasTask>;

export const BiasTaskWithoutId = BiasTask.omit({ taskId: true }).describe(
  'BiasTaskWithoutId',
);

export type BiasTaskWithoutId = z.infer<typeof BiasTaskWithoutId>;

export const BiasTaskIdWithDetails = z
  .object({
    taskId: BiasTaskId,
    taskDetails: BiasTaskWithoutId.nullable(),
  })
  .describe('BiasTaskIdWithDetails');

export type BiasTaskIdWithDetails = z.infer<typeof BiasTaskIdWithDetails>;

export const BiasTaskIdToTaskRecord = z
  .record(BiasTaskId, BiasTaskWithoutId.nullable())
  .describe('BiasTaskIdToTaskRecord');

export type BiasTaskIdToTaskRecord = z.infer<typeof BiasTaskIdToTaskRecord>;

export const BiasTasksByMetricValue = z
  .object({
    framing: MetricDefinitionCalibrationFraming,
    skillIndexFraming: MetricDefinitionCalibrationFraming,
    performanceMetric: z.string().nullable(),
    displayName: z.string(),
    tasks: BiasTaskIdToTaskRecord,
  })
  .describe('BiasTasksByMetricValue');

export type BiasTasksByMetricValue = z.infer<typeof BiasTasksByMetricValue>;

export const BiasTasksByMetric = z
  .record(z.string(), BiasTasksByMetricValue)
  .describe('BiasTasksByMetric');

export type BiasTasksByMetric = z.infer<typeof BiasTasksByMetric>;

export const isBiasTestTaskId = (id: BiasTaskId): id is BiasTestTaskId => {
  return BiasTestTaskIds.includes(id as BiasTestTaskId);
};
